import type moment from 'moment'
import { rem } from 'polished'
import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import DateItem from './DateItem'

import TextButton from 'LuxKit/Button/TextButton'
import LineArrowBreakIcon from 'LuxKit/Icons/line/LineArrowBreakIcon'
import LineCompressLinesIcon from 'LuxKit/Icons/line/LineCompressLinesIcon'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import zIndex from 'styles/tools/z-index'
import {
  ScrollMenu,
} from 'tripPlanner/components/ScrollMenu'
import { ViewModeContext } from 'tripPlanner/contexts/ViewModeContext'
import type { PublicTripItem } from 'tripPlanner/types/publicTrip'
import type { TripItem } from 'tripPlanner/types/tripItem'
import type { ItinerarySection } from 'tripPlanner/utils/itinerary'
import { NO_DATES, SUGGESTED } from 'tripPlanner/utils/itinerary'
import LineHeartClickIcon from 'LuxKit/Icons/line/LineHeartClickIcon'
import LineHeartIcon from 'LuxKit/Icons/line/LineHeartIcon'
import { useHeaderStateObserver } from 'contexts/HeaderStateContext'
import useForwardedRef from 'hooks/useForwardedRef'

const Root = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  box-shadow: ${(props) => props.theme.shadow.bottom.small};
  position: relative;
  z-index: ${zIndex.stickyHeader};
  background: ${(props) => props.theme.palette.neutral.default.eight};
  padding: 0 ${rem(16)};
  gap: ${rem(24)};

  @container (min-width: 640px) {
    padding: 0 ${rem(40)} 0 ${rem(36)};
  }
`

const DateRangeWrapper = styled.div`
  flex: 1;
  min-width: 1px;
  position: relative;
`

const DateRange = styled.div`
  display: block;
  width: 100%;
`

interface Props {
  className?: string
  startDate: moment.Moment
  endDate: moment.Moment
  /** Date in ISO format, or `NO_DATES`/`SUGGESTED` constants */
  selectedSection?: string
  items: Array<TripItem> | Array<PublicTripItem>
  sections: Array<ItinerarySection>
  onClick: (date: string) => void
  style?: React.CSSProperties
  ref?: React.Ref<HTMLDivElement | null>
}

function DateRangeCarousel(props: Props) {
  const {
    className,
    sections,
    selectedSection,
    onClick,
    style,
    ref,
  } = props

  const intl = useIntl()
  const rootRef = useForwardedRef(ref)

  useHeaderStateObserver('tripPlannerDateCarousel', rootRef)

  const viewModeContext = useContext(ViewModeContext)

  const compactLabel = intl.formatMessage({
    id: 'IGgr66s80QYtw9hWszM-',
    defaultMessage: 'Compact',
    description: 'Button label to switch trip planner to compact view mode',
  })
  const detailLabel = intl.formatMessage({
    id: 'r8HeO6XoBAb2n85Uuk_Q',
    defaultMessage: 'Detail',
    description: 'Button label to switch trip planner to detailed view mode',
  })

  const { scrollableSections, hasSuggestedSection, hasSavedSection } = useMemo(() => {
    const sectionSet = new Set(sections)
    const scrollable: Array<moment.Moment> = []

    const hasSuggested = sectionSet.has(SUGGESTED)
    const hasSaved = sectionSet.has(NO_DATES)

    sectionSet.forEach(section => {
      if (section === SUGGESTED || section === NO_DATES) {
        return
      }

      scrollable.push(section)
    })

    return {
      scrollableSections: scrollable,
      hasSuggestedSection: hasSuggested,
      hasSavedSection: hasSaved,
    }
  }, [sections])

  const handleSuggestedClick = useCallback(() => onClick(SUGGESTED), [onClick])

  const handleSavedClick = useCallback(() => onClick(NO_DATES), [onClick])

  const handleDateClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const date = event.currentTarget.dataset.isoDate
    if (date) {
      onClick(date)
    }
  }, [onClick])

  return (
    <Root className={className} style={style} ref={rootRef}>
      {hasSuggestedSection && (
        <DateItem
          id={SUGGESTED}
          key={SUGGESTED}
          mainLabel={<LineHeartClickIcon size="M" />}
          subLabel={intl.formatMessage({
            id: 'hKqSxV7EAlwXvTCKhNt5',
            defaultMessage: 'For you',
            description: 'Label for suggested items section in trip planner date carousel',
          })}
          onClick={handleSuggestedClick}
          selected={selectedSection === SUGGESTED}
        />
      )}
      {hasSavedSection && (
        <DateItem
          id={NO_DATES}
          key={NO_DATES}
          mainLabel={<LineHeartIcon size="M" />}
          subLabel={intl.formatMessage({
            id: '49qRb5VqKAclKiL9g1H5',
            defaultMessage: 'Saved',
            description: 'Label for saved items section in trip planner date carousel',
          })}
          onClick={handleSavedClick}
          selected={selectedSection === NO_DATES}
        />
      )}
      <DateRangeWrapper>
        <DateRange>
          <ScrollMenu selectedId={selectedSection}>
            {scrollableSections.map(section => {
              const dateIso = section.format(ISO_DATE_FORMAT)
              return (
                <DateItem
                  id={dateIso}
                  key={dateIso}
                  mainLabel={section.format('D MMM')}
                  subLabel={section.format('ddd')}
                  data-iso-date={dateIso}
                  onClick={handleDateClick}
                  selected={selectedSection === dateIso}
                />
              )
            })}
          </ScrollMenu>
        </DateRange>
      </DateRangeWrapper>
      <CSSBreakpoint min="desktop">
        <TextButton
          kind="secondary"
          startIcon={
            viewModeContext.viewMode === 'detail' ? (
              <LineCompressLinesIcon />
            ) : (
              <LineArrowBreakIcon />
            )
          }
          onClick={viewModeContext.toggleViewMode}
        >
          {viewModeContext.viewMode === 'detail' ? compactLabel : detailLabel}
        </TextButton>
      </CSSBreakpoint>
    </Root>
  )
}

DateRangeCarousel.displayName = 'DateRangeCarousel'
export default DateRangeCarousel
